import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { type PathPattern, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useTeamSwitcher } from '@sine/sine-team-switcher';

/**
 * Reloads the react-query cache when the current team changes. This is needed because `teamId` is an
 * implicit query param for all queries, but is not captured in the query key as it is stored in the header.
 *
 * Will only activate if the current path matches `pathPattern`.
 * Redirects the user to `redirectPath` after clearing the cache.
 *
 * @param pathPattern The React Router path pattern which is checked against the current route using
 * useMatchPath before reloading. If the path does not match this pattern when the team changes, this hook has no effect.
 * @param redirectPath The path to redirect to after the reload happens.
 */
export function useReloadWhenTeamChanges(pathPattern: PathPattern | string, redirectPath: string) {
  const queryClient = useQueryClient();

  const location = useLocation();
  const navigate = useNavigate();

  const { currentTeam } = useTeamSwitcher();
  const [isReloading, setReloading] = useState<boolean>(false);

  const [prevTeamId, setPrevTeamId] = useState<string | undefined>(currentTeam?.id);

  if (!prevTeamId && currentTeam?.id) {
    setPrevTeamId(currentTeam?.id);
  }

  if (prevTeamId && currentTeam?.id !== prevTeamId && matchPath(pathPattern, location.pathname)) {
    setPrevTeamId(currentTeam?.id);
    setReloading(true);
    queryClient.removeQueries();
    void queryClient.invalidateQueries().finally(() => {
      setReloading(false);
      navigate(redirectPath);
    });
  }

  return { isReloading };
}
